import React from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Breadcrumbs, Snackbar, Alert, Avatar, IconButton, Tooltip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { MetricCard, EntityDrawer, ConfirmDialog, SkeletonCards, ErrorState, PermissionState } from '../ui';
import GAUserForm from './GAUserForm';
import useGACompanyDetailPage from './useGACompanyDetailPage';
import CompanySubscriptionCard from './plans/CompanySubscriptionCard';

const STATUS_COLOR = { active: 'success', suspended: 'error', archived: 'default' };

const GACompanyDetail = () => {
  const p = useGACompanyDetailPage();

  if (p.status === 'loading') {
    return <SkeletonCards rows={5} />;
  }

  if (p.status === 'error') {
    return (
      <ErrorState
        title={p.t('ga.error.generic')}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={p.load}
      />
    );
  }

  if (p.status === 'permission') {
    return <PermissionState title={p.t('ga.error.forbidden')} />;
  }

  const currentStatus = p.company.status || 'active';

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1.5 }}>
        <Link to="/ga/companies" style={{ textDecoration: 'none', color: 'inherit' }}>
          <Typography color="text.secondary" variant="body2">{p.t('ga.companies.title')}</Typography>
        </Link>
        <Typography variant="body2">{p.company.companyName}</Typography>
      </Breadcrumbs>

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          <Avatar variant="rounded" sx={{ width: 56, height: 56, fontSize: (theme) => theme.typography.h5.fontSize, bgcolor: 'primary.light', color: 'primary.dark' }}>
            {p.company.companyName ? p.company.companyName.trim().charAt(0).toUpperCase() : '?'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5">{p.company.companyName}</Typography>
              <Chip label={p.t(`ga.status.${currentStatus}`, { defaultValue: currentStatus })} color={STATUS_COLOR[currentStatus] || 'default'} size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {[p.company.companyEmail, p.company.companyPhone, p.company.companyAddress].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25 }}>
            {(currentStatus === 'active' || currentStatus === undefined) && (
              <Button size="small" variant="outlined" color="error" onClick={() => p.patchStatus('suspended')}>
                {p.t('ga.company.suspend')}
              </Button>
            )}
            {currentStatus !== 'active' && (
              <Button size="small" variant="outlined" color="success" onClick={() => p.patchStatus('active')}>
                {p.t('ga.company.reactivate')}
              </Button>
            )}
            {currentStatus !== 'archived' && (
              <Button size="small" variant="outlined" onClick={() => p.patchStatus('archived')}>
                {p.t('ga.company.archive')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><MetricCard label={p.t('Users')} value={p.metrics.usersCount || 0} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label={p.t('Orders')} value={p.metrics.ordersCount || 0} /></Grid>
        <Grid item xs={6} sm={3}><MetricCard label={p.t('Inventory Items')} value={p.metrics.inventoryCount || 0} /></Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard
            label={p.t('Last Activity')}
            value={p.metrics.lastActivity ? new Date(p.metrics.lastActivity).toLocaleDateString() : p.t('No recent activity')}
          />
        </Grid>
      </Grid>

      {p.subscription && (
        <CompanySubscriptionCard
          data={p.subscription}
          plans={p.plans}
          features={p.features}
          companyName={p.company?.companyName}
          onChange={p.changePlan}
          onGrant={p.grantPlan}
          onRenew={p.renewGrant}
          onRevoke={p.revokeGrant}
          changing={p.changingPlan}
          t={p.t}
        />
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden', maxWidth: 920 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1">{p.t('Users')}</Typography>
          <Button variant="contained" size="small" startIcon={<PersonAddIcon />} onClick={p.openAddUserDrawer}>
            {p.t('ga.company.addUser')}
          </Button>
        </Box>
        {p.users.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">{p.t('No users found.')}</Typography>
          </Box>
        ) : (
          p.users.map((u) => (
            <Box
              key={u._id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 'none' } }}
            >
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'action.selected', color: 'text.secondary' }}>
                {u.username ? u.username.trim().charAt(0).toUpperCase() : '?'}
              </Avatar>
              <Typography variant="subtitle2" sx={{ flex: 1.4, minWidth: 0 }} noWrap>{u.username}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1.6, minWidth: 0 }} noWrap>{u.email}</Typography>
              <Chip label={u.role} size="small" variant={u.role === 'admin' || u.role === 'global_admin' ? 'filled' : 'outlined'} color={u.role === 'admin' || u.role === 'global_admin' ? 'primary' : 'default'} sx={{ textTransform: 'capitalize' }} />
              <Tooltip title={p.t('ga.company.editUser')}>
                <IconButton size="small" aria-label={p.t('ga.company.editUser')} onClick={() => p.openEditUserDrawer(u)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={p.t('ga.company.deleteUser')}>
                <IconButton size="small" color="error" aria-label={p.t('ga.company.deleteUser')} onClick={() => p.openDeleteUser(u)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))
        )}
      </Paper>

      <EntityDrawer
        open={p.userDrawerOpen}
        onClose={p.closeUserDrawer}
        title={p.userDrawerMode === 'edit' ? p.t('ga.company.editUser') : p.t('ga.company.addUser')}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        cancelLabel={p.t('ga.common.cancel')}
        primaryLabel={p.savingUser
          ? (p.userDrawerMode === 'edit' ? p.t('Saving...', { defaultValue: 'Saving...' }) : p.t('Creating...'))
          : p.t('ga.common.save')}
        onPrimary={p.saveUser}
        primaryDisabled={p.savingUser || !p.isUserValid}
        loading={p.savingUser}
      >
        <GAUserForm form={p.userForm} setForm={p.setUserForm} errors={p.userErrors} />
      </EntityDrawer>

      <ConfirmDialog
        open={Boolean(p.deleteTarget)}
        onClose={p.closeDeleteUser}
        title={p.t('ga.company.deleteUser')}
        description={p.t('ga.company.deleteUserConfirm', { username: p.deleteTarget?.username || '' })}
        cancelLabel={p.t('ga.common.cancel')}
        confirmLabel={p.deletingUser ? p.t('Removing...', { defaultValue: 'Removing...' }) : p.t('ga.common.remove')}
        onConfirm={p.confirmDeleteUser}
        loading={p.deletingUser}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GACompanyDetail;
